import {
  endsWith, find, isArray, isObject,
  isString, startsWith,
} from 'lodash';
import { getDateTime } from '@sda/utils/date';
import { normalizeFileName } from '@sda/utils/string';
import { rfc3986EncodeURIComponent } from '@sda/utils/url';
import { CROPPED_IMAGE_PREFIX } from './lib/constants';
import type { ImageInput, ImageSizeUrlCallback, ImageUrlCallback } from './interfaces';
import type { PublicImageUrl } from '@sda/interfaces/image';

/**
 * Construct a URL for the selected image file
 * Note: Defaults to the medium size
 * @param  {string} imagePath
 * @param  {string} size
 * @param  {Date} updatedAt
 * @return {string}
 */
export const getImageUrl: ImageUrlCallback = (
  imagePath: ImageInput,
  size: string = process.env.IMAGE_SIZES_MEDIUM,
  updatedAt: Date = null,
): string => {
  if (imagePath) {
    if (isString(imagePath) && (startsWith(imagePath, 'http') || startsWith(imagePath, '/build'))) {
      if (imagePath.includes('media/home/sidedoorcrop') || imagePath.includes('/media/signup/sidedoorcrop')) {
        let parts = imagePath.split('/');
        let fileName = parts.pop();

        const thumbFileName = `${size}${fileName}`;
        parts.push(thumbFileName);

        const newPath = parts.join('/');

        return newPath;
      }

      return imagePath;
    }

    let path = '';

    if (isString(imagePath)) {
      path = imagePath;
    } else if (isArray(imagePath)) {
      const [firstPath] = imagePath;

      if (firstPath.path) {
        const { path: name } = firstPath;

        path = name;
      }
    } else if (isObject(imagePath)) {
      const { path: name } = imagePath;

      path = name;
    }

    let parts = path.split('/');
    let fileName = parts.pop();

    const thumbFileName = `${size}${fileName}`;
    parts.push(thumbFileName);

    const newPath = parts.join('/');

    const updateString = updatedAt ? `&d=${rfc3986EncodeURIComponent(getDateTime(updatedAt).toSeconds())}` : '';

    return `${process.env.USER_BUCKET_BASE_URL}/${rfc3986EncodeURIComponent(newPath)}?alt=media${updateString}`;
  }

  return null;
};

/**
 * Construct the thumbnail image URL for the selected image file
 * @param  {ImageInput} imagePath
 * @param  {Date} updatedAt
 * @param  {ImageUrlCallback} imageUrlCallback - dependency injection for testing
 * @return {string}
 */
export const getImageThumbUrl: ImageSizeUrlCallback = (
  path: ImageInput,
  updatedAt: Date = null,
  imageUrlCallback = getImageUrl,
): string => imageUrlCallback(path, process.env.IMAGE_SIZES_THUMB, updatedAt);

/**
 * Construct the small image URL for the selected image file
 * @param  {ImageInput} imagePath
 * @param  {Date} updatedAt
 * @param  {ImageUrlCallback} imageUrlCallback - dependency injection for testing
 * @return {string}
 */
export const getImageSmallUrl: ImageSizeUrlCallback = (
  path: ImageInput,
  updatedAt: Date = null,
  imageUrlCallback = getImageUrl,
): string => imageUrlCallback(path, process.env.IMAGE_SIZES_SMALL, updatedAt);

/**
 * Construct the medium image URL for the selected image file
 * @param  {ImageInput} imagePath
 * @param  {Date} updatedAt
 * @param  {ImageUrlCallback} imageUrlCallback - dependency injection for testing
 * @return {string}
 */
export const getImageMediumUrl: ImageSizeUrlCallback = (
  path: ImageInput,
  updatedAt: Date = null,
  imageUrlCallback = getImageUrl,
): string => imageUrlCallback(path, process.env.IMAGE_SIZES_MEDIUM, updatedAt);

/**
 * Construct the large image URL for the selected image file
 * @param  {ImageInput} imagePath
 * @param  {Date} updatedAt
 * @param  {ImageUrlCallback} imageUrlCallback - dependency injection for testing
 * @return {string}
 */
export const getImageLargeUrl: ImageSizeUrlCallback = (
  path: ImageInput,
  updatedAt: Date = null,
  imageUrlCallback = getImageUrl,
): string => imageUrlCallback(path, process.env.IMAGE_SIZES_LARGE, updatedAt);

/**
 * Reconstruct the original image url. Built on top of the existing path builder, so as not to mess with the intricacies
 * of environment vars, etc. that I may not be aware of.
 * @param {string} imagePath
 * @param {Date} updatedAt
 * @param {ImageSizeUrlCallback} imageSizeUrlCallback - dependency injection for testing
 * @return {string}
 */
export const getImageOriginalUrl = (
  imagePath: ImageInput,
  updatedAt: Date = null,
  imageSizeUrlCallback: ImageSizeUrlCallback = getImageLargeUrl,
): string => {
  const large = imageSizeUrlCallback(imagePath, updatedAt);

  const pathParts = large.split('%2F');
  const fileName = pathParts.pop();

  // Slice fileName at 6 to remove "large-".
  // Not using "replace" in case filename contains that anyway.
  return [...pathParts, fileName.slice(6)].join('%2F');
};

/**
 * Takes an image path (in bucket) and returns just the appropriate filename for the cropped version.
 * @param {string} imagePath
 * @return {string}
 */
export const getImageCroppedName = (imagePath: string): string => `${CROPPED_IMAGE_PREFIX}${imagePath.split('/').pop()}`;

/**
 * Take an image path (in bucket) which should be for a cropped image.
 * If so, returns the path for the original.
 * @param {string} imagePath
 * @return {string}
 */
export const getImageUncroppedPath = (imagePath: string): string => {
  if (!imagePath) {
    return imagePath;
  }

  const pathParts = imagePath.split('/');
  const filename = pathParts.pop();

  if (filename.indexOf(CROPPED_IMAGE_PREFIX) === 0) {
    const path = [
      ...pathParts,
      filename.slice(CROPPED_IMAGE_PREFIX.length),
    ].join('/');

    return getImageUncroppedPath(path);
  }

  return imagePath;
};

/**
 * Given an array or string of images, return the image that should be treated as the cover image
 * @param  {array|string} images The array or string of images. Each item in the array should have the properties "path" and "isCover"
 * @param  {string} fallback A string source that the cover should fall back to if no image is found
 * @param  {Function} callback A function that can return an image source. Should be one of the getImage*Url functions
 */
export const getCoverImage = (
  images: ImageInput,
  fallback?: string,
  callback: ImageSizeUrlCallback = getImageMediumUrl,
): string => {
  let image = fallback || '';

  if (images) {
    if (isString(images)) {
      image = callback(images);
    } else if (isArray(images)) {
      const cover = find(images, (imageData: PublicImageUrl) => imageData?.isCover) || images[0];

      if (isString(cover)) {
        image = callback(cover);
      } else if (cover?.path) {
        image = callback(cover.path, cover.updatedAt);
      }
    } else if (isObject(images)) {
      const cover = images;

      image = callback(cover.path, cover.updatedAt);
    }
  }

  return image;
};

/**
 * Takes the given file name and attempts to determine if it's an image
 * @param  {string} fileName
 * @return {boolean}
 */
export const isImage = (fileName: string): boolean => {
  const normalizedSrc = normalizeFileName(fileName);
  return endsWith(normalizedSrc, 'jpg') || endsWith(normalizedSrc, 'gif') || endsWith(normalizedSrc, 'png') || endsWith(normalizedSrc, 'jpeg');
};
